import { createTheme, SxProps, Theme } from '@mui/material/styles';
import { itIT } from '@mui/x-data-grid-premium';
import { deepmerge } from '@mui/utils';
import {
  filterOperators,
  numberFilterOperators,
  dateFilterOperators,
  booleanFilterOperators,
  singleSelectFilterOperators
} from 'components/enhanced/';

const baseThemePalette = {
  palette: {
    success: {
      main: '#4CBA53'
    },
    error: {
      main: '#EF4933'
    },
    warning: {
      main: '#DDA059'
    },
    info: {
      main: '#87CCB9'
    },
    // Used by `getContrastText()` to maximize the contrast between
    // the background and the text.
    contrastThreshold: 3,
    // Used by the functions below to shift a color's luminance by approximately
    // two indexes within its tonal palette.
    // E.g., shift from Red 500 to Red 300 or Red 700.
    tonalOffset: 0.15
  }
};

const BRAND_PRIMARY = '#2479B1';

export const advancedFilterPanelStyles: SxProps<Theme> = {
  // Contenitore Card
  '& .MuiPaper-root': {
    borderRadius: '12px',
    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e2e8f0',
    padding: '16px'
  },
  // Righe dei filtri affiancati
  '& .MuiDataGrid-filterForm': {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '8px 0'
  },

  // Input Outlined con bordi arrotondati
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '13px',
    color: '#1e293b',
    '& fieldset': { borderColor: '#cbd5e1' },
    '&:hover fieldset': { borderColor: '#94a3b8' },
    '&.Mui-focused fieldset': { borderColor: BRAND_PRIMARY }
  },

  // Etichette incastonate nel bordo
  '& .MuiInputLabel-outlined': {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '13px',
    color: '#64748b',
    backgroundColor: '#ffffff',
    padding: '0 4px',
    '&.Mui-focused': { color: BRAND_PRIMARY }
  },
  // Footer con disposizione space-between
  '& .MuiDataGrid-panelFooter': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center', // Centra verticalmente il testo nativo e il tuo bottone
    borderTop: 'none',
    marginTop: '20px', // Aumenta lo stacco tra l'ultima riga di filtri e il footer
    padding: '0',

    // Pulsante "+ Aggiungi Filtro"
    '& .MuiButtonBase-root': {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 500,
      fontSize: '13px',
      lineHeight: '1.2', // 120%
      letterSpacing: '0em',
      textTransform: 'none',
      color: BRAND_PRIMARY,
      '&:hover': { backgroundColor: 'rgba(0, 109, 176, 0.04)' }
    }
  },
  // Pulsante pieno "Applica filtri"
  '& .MuiDataGrid-panelFooter .MuiButton-containedPrimary': {
    backgroundColor: BRAND_PRIMARY,
    color: '#ffffff',
    borderRadius: '6px',
    padding: '6px 16px',
    boxShadow: 'none',
    '&:hover': { backgroundColor: '#00558a', boxShadow: 'none' }
  }
};

const tabThemeOverrides = {
  MuiTab: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        textTransform: 'uppercase',
        fontWeight: 500,
        fontSize: '0.85rem',
        letterSpacing: '0.5px',

        // Questo seleziona le tab a cui aggiungerai la classe 'MuiTab-admin'
        '&.MuiTab-admin': {
          color: '#2578b0',
          fontWeight: 'bold',
          '&.Mui-selected': {
            color: '#29456c',
            backgroundColor: theme.palette.action.hover
          }
        }
      })
    }
  }
};

export const dataGridThemeOverrides = {
  // 1. GLOBAL OVERRIDES FOR TARGET COMPONENTS (Replaces raw CSS classes)
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        margin: 0,
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale'
      },
      code: {
        fontFamily: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace"
      }
    }
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        // Clears out conflicting background declarations natively through the pipeline
        backgroundColor: '#ffffff',
        background: 'transparent'
      }
    }
  },
  MuiDataGrid: {
    defaultProps: {
      border: 'none',
      localeText: {
        ...itIT.components.MuiDataGrid.defaultProps.localeText,
        filterPanelAddFilter: 'Aggiungi Filtro',
        filterPanelRemoveAll: 'Pulisci tutto'
      },
      slotProps: {
        panel: { sx: advancedFilterPanelStyles },
        filterPanel: {
          filterFormProps: {
            logicOperatorInputProps: { variant: 'outlined' as const, size: 'small' as const },
            columnInputProps: {
              variant: 'outlined' as const,
              size: 'small' as const,
              InputLabelProps: { shrink: true }
            },
            operatorInputProps: {
              variant: 'outlined' as const,
              size: 'small' as const,
              InputLabelProps: { shrink: true }
            },
            valueInputProps: {
              InputComponentProps: {
                variant: 'outlined' as const,
                size: 'small' as const
              }
            }
          }
        }
      }
    },
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => {
        return {
          border: 'none',
          width: '100%',
          height: '100%',
          minHeight: 350,
          backgroundColor: '#ffffff',

          '& .MuiDataGrid-columnHeader': { backgroundColor: '#F4F4F4' },

          // 2. CELL OVERRIDES INTEGRATED FROM GLOBAL CSS (Without needing !important flags)
          '& .MuiDataGrid-cell': {
            cursor: 'default',
            // Targets typography elements nested cleanly within cells
            '& > *': {
              color: '#555555'
            },

            // Target explicit cell states (Editable rows context formatting)
            '&.MuiDataGrid-cell--editable': {
              cursor: 'text',
              '& > *': {
                color: '#282c34',
                fontWeight: 700,
                fontStyle: 'italic'
              }
            }
          },

          // 3. AGGREGATE SYSTEM GLOBAL SHAPE RESETS (Replaces * { border-radius: 0px !important })
          // Scopes the border-radius zeroing specifically onto the table elements for clean presentation
          borderRadius: 0,
          '& *': {
            borderRadius: 0
          },

          '& .MuiDataGrid-overlayWrapper': {
            minHeight: 200
          }
        };
      }
    }
  }
};

const dialogThemeOverrides = {
  // Struttura esterna e ombreggiatura del foglio Dialog
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: '12px',
        backgroundImage: 'none', // Previene artefatti visivi nativi di MUI
        boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
      }
    }
  },
  // Intestazione (Header)
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        color: '#29456c',
        padding: '16px 24px',
        backgroundColor: '#ffffff'
      }
    }
  },
  // Corpo Centrale per lo scorrimento dei campi (Content)
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '24px', // Corrisponde a p: 3
        backgroundColor: '#ffffff',
        '&:first-of-type': {
          paddingTop: '24px' // Risolve il bug del padding ridotto quando manca il titolo
        }
      }
    }
  },
  // Barra delle Azioni inferiore (Footer)
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px', // Allineamento perfetto al millimetro con l'Header
        gap: '8px'
      }
    }
  }
};

const globalComponents = deepmerge(
  deepmerge(dataGridThemeOverrides, dialogThemeOverrides),
  tabThemeOverrides
);

const labTheme = createTheme({
  palette: {
    ...baseThemePalette,
    primary: {
      main: '#29456c'
    },
    secondary: {
      main: '#2578b0'
    }
  },
  components: globalComponents
});

const refertingTheme = createTheme({
  palette: {
    ...baseThemePalette,
    primary: {
      main: '#0791e7'
    },
    secondary: {
      main: '#2578b0'
    }
  },
  components: globalComponents
});

const evaluateTheme = createTheme({
  palette: {
    ...baseThemePalette,
    primary: {
      main: '#0163ae'
    },
    secondary: {
      main: '#2578b0'
    }
  },
  components: globalComponents
});

const settingsTheme = createTheme({
  palette: {
    ...baseThemePalette,
    primary: {
      main: '#64b4fe'
    },
    secondary: {
      main: '#2578b0'
    }
  },
  components: globalComponents
});

const popoverTheme = createTheme({
  components: {
    MuiPopover: {
      styleOverrides: {
        paper: {
          width: '360px',
          maxWidth: '400px',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
          border: '1px solid #e2e6ea',
          marginTop: '8px'
        }
      }
    }
  }
});

export { labTheme, refertingTheme, evaluateTheme, settingsTheme, popoverTheme };
